<div
    [formGroup]="formGroup"
    [ngClass]="{
        'grid grid-cols-1 gap-4': requirementPosition === 'top' || requirementPosition === 'bottom',
        'grid grid-cols-1 lg:grid-cols-2 gap-6 items-start': requirementPosition === 'left' || requirementPosition === 'right'
    }">

    <ng-template #passwordInputs>
        <div class="grid grid-cols-1 gap-4">
            <mat-form-field *ngIf="showOldPassword" class="w-full">
                <mat-label>{{oldPasswordLabel}}</mat-label>
                <input
                    matInput
                    type="password"
                    [formControlName]="oldPasswordControl"
                    #oldPasswordField>
                <button
                    mat-icon-button
                    type="button"
                    (click)="oldPasswordField.type === 'password' ? oldPasswordField.type = 'text' : oldPasswordField.type = 'password'"
                    matSuffix>
                    <mat-icon
                        class="icon-size-5"
                        *ngIf="oldPasswordField.type === 'password'"
                        [svgIcon]="'heroicons_solid:eye'"></mat-icon>
                    <mat-icon
                        class="icon-size-5"
                        *ngIf="oldPasswordField.type === 'text'"
                        [svgIcon]="'heroicons_solid:eye-off'"></mat-icon>
                </button>
                <mat-error *ngIf="hasControlError(oldPasswordControl, 'required')">
                    {{oldPasswordLabel}} is required
                </mat-error>
            </mat-form-field>

            <mat-form-field class="w-full">
                <mat-label>{{newPasswordLabel}}</mat-label>
                <input
                    matInput
                    [type]="isNewPasswordVisible ? 'text' : 'password'"
                    [formControlName]="newPasswordControl">
                <button
                    mat-icon-button
                    type="button"
                    (click)="toggleNewPasswordVisibility()"
                    matSuffix>
                    <mat-icon
                        class="icon-size-5"
                        *ngIf="!isNewPasswordVisible"
                        [svgIcon]="'heroicons_solid:eye'"></mat-icon>
                    <mat-icon
                        class="icon-size-5"
                        *ngIf="isNewPasswordVisible"
                        [svgIcon]="'heroicons_solid:eye-off'"></mat-icon>
                </button>
                <mat-error *ngIf="hasControlError(newPasswordControl, 'required')">
                    {{newPasswordLabel}} is required
                </mat-error>
                <mat-error *ngIf="hasControlError(newPasswordControl, 'minlength')">
                    {{newPasswordLabel}} must be at least {{minLength}} characters
                </mat-error>
            </mat-form-field>

            <mat-form-field *ngIf="!isNewPasswordVisible" class="w-full">
                <mat-label>{{confirmPasswordLabel}}</mat-label>
                <input
                    matInput
                    [type]="isConfirmPasswordVisible ? 'text' : 'password'"
                    [formControlName]="confirmPasswordControl">
                <button
                    mat-icon-button
                    type="button"
                    (click)="toggleConfirmPasswordVisibility()"
                    matSuffix>
                    <mat-icon
                        class="icon-size-5"
                        *ngIf="!isConfirmPasswordVisible"
                        [svgIcon]="'heroicons_solid:eye'"></mat-icon>
                    <mat-icon
                        class="icon-size-5"
                        *ngIf="isConfirmPasswordVisible"
                        [svgIcon]="'heroicons_solid:eye-off'"></mat-icon>
                </button>
                <mat-error *ngIf="hasControlError(confirmPasswordControl, 'required')">
                    {{confirmPasswordLabel}} is required
                </mat-error>
                <mat-error *ngIf="hasPasswordMismatch()">
                    Passwords must match
                </mat-error>
            </mat-form-field>
        </div>
    </ng-template>

    <ng-template #passwordSubmit>
        <div
            class="flex items-center gap-3"
            [ngClass]="{ 'lg:col-span-2': requirementPosition === 'right' }"
            [class.w-full]="requirementPosition === 'right' || requirementPosition === 'top'"
            [class.justify-end]="requirementPosition === 'right'">
            <button
                type="button"
                mat-flat-button
                [class.w-full]="requirementPosition === 'top'"
                [color]="'primary'"
                [disabled]="isSubmitDisabled"
                (click)="onSubmit()">
                <span *ngIf="!isSubmitting">{{submitButtonLabel}}</span>
                <mat-progress-spinner
                    *ngIf="isSubmitting"
                    [diameter]="22"
                    [mode]="'indeterminate'"></mat-progress-spinner>
            </button>
        </div>
    </ng-template>

    <ng-template #requirements>
        <div class="p-4 bg-gray-100 dark:bg-gray-800 rounded-lg">
            <div class="text-sm font-semibold mb-3">Password Requirements:</div>
            <div class="space-y-2 text-sm">
                <div class="flex items-center" [class.text-green-600]="checkPasswordRequirement('length')" [class.text-secondary]="!checkPasswordRequirement('length')">
                    <mat-icon class="icon-size-4 mr-2" [svgIcon]="checkPasswordRequirement('length') ? 'heroicons_solid:check-circle' : 'heroicons_outline:question-mark-circle'"></mat-icon>
                    <span>At least {{minLength}} characters long</span>
                </div>
                <div class="flex items-center" [class.text-green-600]="checkPasswordRequirement('uppercase')" [class.text-secondary]="!checkPasswordRequirement('uppercase')">
                    <mat-icon class="icon-size-4 mr-2" [svgIcon]="checkPasswordRequirement('uppercase') ? 'heroicons_solid:check-circle' : 'heroicons_outline:question-mark-circle'"></mat-icon>
                    <span>At least one uppercase letter</span>
                </div>
                <div class="flex items-center" [class.text-green-600]="checkPasswordRequirement('lowercase')" [class.text-secondary]="!checkPasswordRequirement('lowercase')">
                    <mat-icon class="icon-size-4 mr-2" [svgIcon]="checkPasswordRequirement('lowercase') ? 'heroicons_solid:check-circle' : 'heroicons_outline:question-mark-circle'"></mat-icon>
                    <span>At least one lowercase letter</span>
                </div>
                <div class="flex items-center" [class.text-green-600]="checkPasswordRequirement('number')" [class.text-secondary]="!checkPasswordRequirement('number')">
                    <mat-icon class="icon-size-4 mr-2" [svgIcon]="checkPasswordRequirement('number') ? 'heroicons_solid:check-circle' : 'heroicons_outline:question-mark-circle'"></mat-icon>
                    <span>At least one number</span>
                </div>
                <div class="flex items-center" [class.text-green-600]="checkPasswordRequirement('special')" [class.text-secondary]="!checkPasswordRequirement('special')">
                    <mat-icon class="icon-size-4 mr-2" [svgIcon]="checkPasswordRequirement('special') ? 'heroicons_solid:check-circle' : 'heroicons_outline:question-mark-circle'"></mat-icon>
                    <span>At least one special character (!@#$%^&*)</span>
                </div>
                <div *ngIf="!isNewPasswordVisible" class="flex items-center" [class.text-green-600]="arePasswordsEqual()" [class.text-secondary]="!arePasswordsEqual()">
                    <mat-icon class="icon-size-4 mr-2" [svgIcon]="arePasswordsEqual() ? 'heroicons_solid:check-circle' : 'heroicons_outline:question-mark-circle'"></mat-icon>
                    <span>New password and confirm password must match</span>
                </div>
            </div>
        </div>
    </ng-template>

    <ng-container *ngIf="requirementPosition === 'top'">
        <div class="mb-4">
            <ng-container [ngTemplateOutlet]="requirements"></ng-container>
        </div>
        <ng-container [ngTemplateOutlet]="passwordInputs"></ng-container>
        <ng-container [ngTemplateOutlet]="passwordSubmit"></ng-container>
    </ng-container>

    <ng-container *ngIf="requirementPosition === 'bottom'">
        <ng-container [ngTemplateOutlet]="passwordInputs"></ng-container>
        <ng-container [ngTemplateOutlet]="requirements"></ng-container>
        <ng-container [ngTemplateOutlet]="passwordSubmit"></ng-container>
    </ng-container>

    <ng-container *ngIf="requirementPosition === 'left'">
        <ng-container [ngTemplateOutlet]="requirements"></ng-container>
        <ng-container [ngTemplateOutlet]="passwordInputs"></ng-container>
        <ng-container [ngTemplateOutlet]="passwordSubmit"></ng-container>
    </ng-container>

    <ng-container *ngIf="requirementPosition === 'right'">
        <ng-container [ngTemplateOutlet]="passwordInputs"></ng-container>
        <ng-container [ngTemplateOutlet]="requirements"></ng-container>
        <ng-container [ngTemplateOutlet]="passwordSubmit"></ng-container>
    </ng-container>
</div>